import { type ClientTrackRequest, screenToken } from "@studyward/shared";
import { useEffect } from "react";
import { matchPath, useLocation } from "react-router";
import { z } from "zod";
import { api } from "./api";

/**
 * App-only analytics events (spec §11.5, plan BE1) — screen views, onboarding steps and client-side gates.
 * Fire-and-forget: never blocks or fails the page that sends it.
 */
const ANON_KEY = "studyward.anonymous-id.v1";

/** A random v4 id kept in this browser only, so steps before sign-up can be counted and linked after it. */
function browserId(): string | undefined {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

export function trackClient(event: ClientTrackRequest["event"], properties: ClientTrackRequest["properties"] = {}): void {
  void api.post("/analytics/track", z.unknown(), { event, properties, anonymous_id: browserId() }).catch(() => undefined);
}

export const onboardingStep = {
  viewed: (step: string) => trackClient("onboarding_step_viewed", { step }),
  completed: (step: string) => trackClient("onboarding_step_completed", { step }),
};

/** Route patterns of App.tsx, so screen views never carry ids. */
const PATTERNS = [
  "/signin",
  "/signup",
  "/auth/callback",
  "/auth/new-password",
  "/shares/preview/:token",
  "/onboarding",
  "/dashboard",
  "/upload",
  "/sets",
  "/sets/:setId",
  "/sets/:setId/hoot",
  "/sets/:setId/guide",
  "/sets/:setId/mind-map",
  "/sets/:setId/review",
  "/sets/:setId/quiz",
  "/review",
  "/practice/:sessionId",
  "/sessions/:sessionId/complete",
  "/mock-exams/new",
  "/mock-exams/:examId",
  "/mock-exams/:examId/results",
  "/progress",
  "/groups",
  "/groups/join/:token",
  "/groups/:groupId",
  "/billing",
  "/settings",
  "/support",
  "/support/:ticketId",
];

/** Records a screen_viewed for every route change (renders nothing). */
export function ScreenTracking() {
  const { pathname } = useLocation();
  const pattern = PATTERNS.find((p) => matchPath(p, pathname)) ?? "not_found";
  useEffect(() => trackClient("screen_viewed", { screen: screenToken(pattern) }), [pattern]);
  return null;
}
